import React, { useState } from "react";

const RHCE = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Prepare for the Red
          Hat Certified Engineer (RHCE) certification by mastering advanced
          Linux system administration, automation, and troubleshooting on Red
          Hat Enterprise Linux (RHEL).
          <p className="font-bold text-sm">Duration:</p> 45 days, 1 hour per
          day.
          <p className="font-bold text-sm">Prerequisites:</p> RHCSA
          certification or equivalent experience; familiarity with RHEL 8/9
          system administration.
          <p className="font-bold text-sm">Tools Needed:</p> A computer with
          virtualization software (e.g., VirtualBox, VMware), Red Hat Enterprise
          Linux 8/9, Ansible, and internet access.
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to RHCE and Ansible",
          objective: "Understand RHCE requirements and introduce Ansible.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of RHCE certification and exam objectives.</li>
                <li>Introduction to Ansible and its architecture.</li>
                <li>Installing Ansible on RHEL.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install Ansible and verify the setup.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Ansible Basics – Inventory and Ad-Hoc Commands",
          objective: "Learn to manage inventory and run ad-hoc commands.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding Ansible inventory files.</li>
                <li>Running ad-hoc commands with ansible.</li>
                <li>Configuring SSH for Ansible communication.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Run an ad-hoc command to check system uptime.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Ansible Playbooks – Basics",
          objective: "Learn to write and execute basic Ansible playbooks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to YAML and playbooks.</li>
                <li>Creating a simple playbook (e.g., install packages).</li>
                <li>Running playbooks with ansible-playbook.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a playbook to install Apache.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: Ansible Variables and Facts",
          objective: "Understand variables and system facts in Ansible.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Defining and using variables in playbooks.</li>
                <li>Collecting system facts with setup module.</li>
                <li>Using conditionals with variables.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Create a playbook using system facts to configure a server.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Ansible Loops and Conditionals",
          objective: "Learn to use loops and conditionals in playbooks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using loops (with_items, loop).</li>
                <li>Implementing conditionals (when, unless).</li>
                <li>Handling errors with ignore_errors.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a playbook with a loop to create multiple users.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Ansible Roles – Basics",
          objective: "Learn to create and use Ansible roles.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to roles and their structure.</li>
                <li>Creating a basic role for a task (e.g., web server).</li>
                <li>Using roles in playbooks.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a role to install and configure Apache.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Ansible Roles – Advanced",
          objective: "Explore advanced role features.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using dependencies between roles.</li>
                <li>Passing parameters to roles.</li>
                <li>Organizing roles with best practices.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Develop a role with dependencies for a multi-service setup.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Configuring Web Servers (Apache)",
          objective: "Learn to configure and secure Apache web servers.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Installing and configuring Apache (httpd).</li>
                <li>Setting up virtual hosts.</li>
                <li>Securing Apache with SSL/TLS.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a virtual host with SSL.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Configuring DNS Servers (Bind)",
          objective: "Learn to configure a DNS server using Bind.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to DNS and Bind.</li>
                <li>Configuring forward and reverse zones.</li>
                <li>Testing DNS resolution.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a simple DNS server with a forward zone.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: Configuring Email Servers (Postfix)",
          objective: "Learn to configure a basic email server with Postfix.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Postfix and email protocols (SMTP).</li>
                <li>Installing and configuring Postfix.</li>
                <li>Testing email delivery.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure Postfix to send test emails.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Configuring File Sharing with NFS",
          objective: "Learn to set up an NFS server for file sharing.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding NFS (Network File System).</li>
                <li>Configuring an NFS server and client.</li>
                <li>Securing NFS exports.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Share a directory using NFS with client access.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: Configuring Samba for File Sharing",
          objective: "Learn to configure a Samba file server.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Samba and SMB protocol.</li>
                <li>Configuring Samba shares.</li>
                <li>Accessing shares from Windows/Linux clients.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a Samba share and test access.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: Configuring iSCSI Storage",
          objective: "Learn to configure iSCSI target and initiator.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to iSCSI and block storage.</li>
                <li>Setting up an iSCSI target.</li>
                <li>Configuring an iSCSI initiator.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure an iSCSI target and connect an initiator.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: Network Time Protocol (NTP) Configuration",
          objective: "Learn to configure time synchronization with NTP.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding NTP and chrony.</li>
                <li>Configuring chrony as an NTP client/server.</li>
                <li>Verifying time synchronization.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a system as an NTP client.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Configuring Firewall with firewalld",
          objective: "Master firewall configuration with firewalld.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Advanced firewalld zones and services.</li>
                <li>Configuring rich rules.</li>
                <li>Backing up and restoring firewall configurations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a rich rule to allow specific traffic.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: SELinux – Advanced Configuration",
          objective: "Learn advanced SELinux administration.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Managing SELinux policies (semanage).</li>
                <li>Creating custom SELinux modules.</li>
                <li>Troubleshooting SELinux denials.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a custom SELinux policy for a service.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: Shell Scripting for Automation",
          objective: "Learn to write shell scripts for automation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Basics of Bash scripting (variables, loops).</li>
                <li>Using conditionals and functions.</li>
                <li>Automating system tasks (e.g., backups).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a script to automate a system check.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: Configuring Network Bonding",
          objective: "Learn to configure network bonding for redundancy.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding network bonding modes.</li>
                <li>Configuring bonding with nmcli.</li>
                <li>Testing bond failover.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a network bond with two interfaces.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Configuring Network Teaming",
          objective: "Learn to configure network teaming.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to network teaming.</li>
                <li>Configuring teaming with nmcli.</li>
                <li>Comparing bonding and teaming.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a network team and test connectivity.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Managing Kernel Modules",
          objective: "Learn to manage and tune kernel modules.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding kernel modules (lsmod, modinfo).</li>
                <li>Loading and unloading modules (insmod, rmmod).</li>
                <li>Configuring module options.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Load a kernel module and configure its parameters.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Configuring MariaDB Database",
          objective: "Learn to set up and manage a MariaDB database.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Installing and configuring MariaDB.</li>
                <li>Creating and managing databases/tables.</li>
                <li>Securing MariaDB with users and privileges.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a database and insert sample data.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Configuring PostgreSQL Database",
          objective: "Learn to set up and manage a PostgreSQL database.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Installing and configuring PostgreSQL.</li>
                <li>Creating and managing databases/tables.</li>
                <li>Securing PostgreSQL with roles and permissions.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a PostgreSQL database and test queries.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: Automating with Ansible Vault",
          objective: "Learn to secure sensitive data with Ansible Vault.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Ansible Vault.</li>
                <li>Encrypting and decrypting variables.</li>
                <li>Using Vault in playbooks.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Encrypt a password and use it in a playbook.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Troubleshooting Network Services",
          objective: "Learn to troubleshoot network-related issues.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Diagnosing DNS, HTTP, and SMTP issues.</li>
                <li>Using tcpdump and wireshark for packet analysis.</li>
                <li>Restoring service configurations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Troubleshoot a misconfigured web server.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Troubleshooting Storage Issues",
          objective: "Learn to troubleshoot storage problems.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Diagnosing LVM and file system issues.</li>
                <li>Recovering from disk failures.</li>
                <li>Restoring backups.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Recover a corrupted file system using backups.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Performance Tuning",
          objective: "Learn to tune system performance.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Monitoring performance with sar and iostat.</li>
                <li>Tuning kernel parameters (sysctl).</li>
                <li>Optimizing memory and CPU usage.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Adjust a kernel parameter to improve performance.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Managing Containers with Podman – Advanced",
          objective: "Explore advanced container management with Podman.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Building custom container images.</li>
                <li>Managing container networks.</li>
                <li>Running multi-container setups.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build and run a custom container image.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Configuring High Availability with Keepalived",
          objective: "Learn to configure high availability with Keepalived.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Keepalived and VRRP.</li>
                <li>Configuring a virtual IP for failover.</li>
                <li>Testing high availability.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a Keepalived cluster with a virtual IP.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Final Project – Planning",
          objective: "Plan the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>Suggested project ideas (e.g., Automated Web Cluster).</li>
                <li>Planning features and structure.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and outline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Final Project – Development (Part 1)",
          objective: "Develop the initial setup of the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up the project environment.</li>
                <li>Implementing core services (e.g., web server).</li>
                <li>Configuring Ansible automation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Start building the project infrastructure.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 31: Final Project – Development (Part 2)",
          objective: "Continue developing the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Adding advanced features (e.g., SSL, HA).</li>
                <li>Integrating monitoring and backups.</li>
                <li>Testing configurations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Enhance the project with additional features.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 32: Final Project – Testing and Debugging",
          objective: "Test and debug the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Testing all components (network, services).</li>
                <li>Troubleshooting common issues.</li>
                <li>Optimizing performance.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Test and fix issues in the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 33: Final Project – Documentation",
          objective: "Document the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating a project README.</li>
                <li>Documenting configurations and scripts.</li>
                <li>Preparing a presentation outline.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write documentation for the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 34: Preparing for RHCE Exam – Automation",
          objective: "Review Ansible and automation skills.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review playbooks, roles, and variables.</li>
                <li>Practice complex automation tasks.</li>
                <li>Mock scenarios for automation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete a mock automation task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 35: Preparing for RHCE Exam – Services",
          objective: "Review service configuration skills.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review web, DNS, and email server setups.</li>
                <li>Practice securing services with SELinux/firewalld.</li>
                <li>Mock service configuration scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a mock web server setup.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 36: Preparing for RHCE Exam – Troubleshooting",
          objective: "Review troubleshooting techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review network, storage, and service troubleshooting.</li>
                <li>Practice using diagnostic tools (tcpdump, journalctl).</li>
                <li>Mock troubleshooting scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Resolve a mock network issue.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 37: Final Project – Rehearsal",
          objective: "Rehearse the final project presentation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a demo walkthrough.</li>
                <li>Anticipating questions and challenges.</li>
                <li>Practicing the presentation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Rehearse the project demo.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 38: Final Project – Peer Review",
          objective: "Receive feedback on the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Presenting to peers for feedback.</li>
                <li>Discussing improvements and optimizations.</li>
                <li>Finalizing the project.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present and refine the project based on feedback.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 39: Exam Strategy and Tips",
          objective: "Learn strategies for the RHCE exam.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding the exam format and timing.</li>
                <li>Time management and prioritization tips.</li>
                <li>Reviewing common pitfalls and solutions.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Practice a timed mock exam task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 40: Mock Exam – Automation",
          objective: "Practice automation tasks for the RHCE exam.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mock scenario: Automate server setup.</li>
                <li>Reviewing Ansible best practices.</li>
                <li>Self-assessment and feedback.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete the mock automation task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 41: Mock Exam – Service Configuration",
          objective: "Practice service configuration tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mock scenario: Configure a web and DNS server.</li>
                <li>Reviewing security configurations.</li>
                <li>Self-assessment and feedback.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete the mock service configuration task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 42: Mock Exam – Troubleshooting",
          objective: "Practice troubleshooting tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mock scenario: Troubleshoot network and storage issues.</li>
                <li>Reviewing diagnostic tools.</li>
                <li>Self-assessment and feedback.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete the mock troubleshooting task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 43: Final Project – Final Adjustments",
          objective: "Finalize the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Reviewing project requirements.</li>
                <li>Making final tweaks and optimizations.</li>
                <li>Preparing for presentation day.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Finalize the project setup.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 44: Final Project – Presentation Rehearsal",
          objective: "Rehearse the final project presentation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Practicing the full demo.</li>
                <li>Anticipating questions from the audience.</li>
                <li>Refining the presentation flow.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Rehearse the presentation with a timer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 45: Final Project – Presentation",
          objective: "Present the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Delivering the project demo.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Receiving feedback from peers and instructor.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed project.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Automate system administration tasks using Ansible.</li>
            <li>
              Configure and manage advanced network services (web, DNS, email).
            </li>
            <li>Secure and troubleshoot RHEL systems effectively.</li>
            <li>Manage high availability and performance tuning.</li>
            <li>Prepare for and pass the RHCE certification exam.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">Automated Web Cluster:</span>{" "}
              Set up a cluster of web servers with Ansible automation, SSL, and
              load balancing.
            </li>
            <li>
              <span className="font-bold text-sm">
                Multi-Service Environment:
              </span>{" "}
              Configure a system with web, DNS, and email services, secured with
              SELinux and firewalld.
            </li>
            <li>
              <span className="font-bold text-sm">
                High Availability File Server:
              </span>{" "}
              Implement a highly available NFS or Samba server with Keepalived
              and backups.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              Practice with Red Hat’s official Ansible labs or RHCE practice
              exams.
            </li>
            <li>
              Review the RHCE exam objectives regularly (available on Red Hat’s
              website).
            </li>
            <li>
              Use discussion time for troubleshooting complex scenarios and Q&A.
            </li>
          </ul>
          <p className="text-sm">
            This syllabus covers advanced RHCE topics over 45 days, preparing
            learners for certification and advanced system engineering roles.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-red-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">RHCE Syllabus</h1>
        <p className="text-sm text-white">
          Master Red Hat Certified Engineer (45 Hours – 1 Hour Per Day)
        </p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RHCE;
